import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { sortBy } from 'lodash';
import type { User } from '../../store/types.js';
import type { OrganizationWithData } from 'src/app-state-dashboard.js';
import { getOrganizationUrl } from 'src/store/selectors';
import { joinWithAnd } from 'src/utilities/text.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { dateTimeDisplayShort } from 'src/store/utilities.js';
import { t } from 'src/i18n';

/**
 * Dashboard variant av organisasjonsvelger med mer informasjon.
 * Uten "Legg til ny organisasjon" knapp.
 */
@customElement('d-select-organization-dashboard')
export class DSelectOrganizationDashboard extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    * {
      margin: 0;
      padding: 0;
      outline: 0;
      -webkit-text-size-adjust: 100%;
      -webkit-margin-before: 0;
      -webkit-margin-after: 0;
      -webkit-margin-start: 0;
      -webkit-margin-end: 0;
      -webkit-padding-start: 0;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    #accountSelector ul {
      list-style-type: none;
      margin: 0 0 12px 0;
      font-weight: 400;
    }

    #accountSelector li {
      margin-bottom: 8px;
    }

    #accountSelector li a {
      display: block;
      padding: 16px 18px;
      color: var(--linkColorGray);
      background-color: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 8px;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    #accountSelector li a:hover,
    #accountSelector li a:active {
      color: white;
      background-color: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.25);
      transform: translateY(-1px);
    }

    .org-name {
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 6px;
      color: white;
    }

    .org-info {
      font-size: 14px;
      opacity: 0.8;
      line-height: 1.6;
      min-height: 22px;
    }

    .org-info-row {
      margin-bottom: 2px;
    }

    .org-status {
      display: inline-block;
      margin-top: 6px;
      padding: 3px 8px;
      font-size: 12px;
      font-weight: 500;
      border-radius: 4px;
      background-color: rgba(255, 255, 255, 0.15);
    }

    .org-status.closed {
      background-color: rgba(255, 100, 100, 0.3);
    }

    .org-status.pending {
      background-color: rgba(255, 200, 0, 0.3);
    }

    .skeleton {
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.1) 25%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0.1) 75%
      );
      background-size: 200% 100%;
      animation: loading 1.5s ease-in-out infinite;
      border-radius: 4px;
      height: 18px;
      width: 70%;
      display: inline-block;
    }

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    .noOrgMessage {
      text-align: center;
      padding: 40px 20px;
      opacity: 0.7;
    }

    .meeting-stats-container {
      padding: 12px 0;
    }

    .meeting-stats-header {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 10px;
      color: rgba(255, 255, 255, 0.95);
    }

    .stats-total {
      font-size: 11px;
      font-weight: 400;
      opacity: 0.7;
    }

    .meeting-stats-row {
      display: flex;
      gap: 10px;
      align-items: stretch;
    }

    .stat-box {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 16px 8px;
      background-color: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      min-width: 0;
      min-height: 80px;
      box-sizing: border-box;
    }

    .stat-number {
      font-size: 32px;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 8px;
      color: rgba(255, 255, 255, 0.95);
    }

    .stat-label {
      font-size: 11px;
      opacity: 0.8;
      text-align: center;
      line-height: 1.3;
      max-width: 100%;
      word-wrap: break-word;
    }

    .stat-alert-box {
      background-color: rgba(255, 100, 100, 0.2);
      border-color: rgba(255, 100, 100, 0.4);
    }

    .stat-alert-box .stat-number {
      color: rgba(255, 150, 150, 1);
    }

    .stat-separator {
      width: 1px;
      background-color: rgba(255, 255, 255, 0.3);
      margin: 0 4px;
    }
  `;

  @property({ type: Array })
  organizations: OrganizationWithData[] = [];

  @property({ type: Number })
  organizationId = 0;

  @property({ type: Object })
  user!: User;

  get sortedOrganizations() {
    const a = this.organizations.slice();
    return sortBy(a, [(item) => (item.name ? item.name.toUpperCase() : '')]);
  }

  private renderEmployees(org: OrganizationWithData) {
    // For contact persons, just show they are a contact
    if (org.userType === 'CONTACT') {
      return html`<div class="org-info-row">${t('account.selectOrganizationDashboard.contactPerson')}</div>`;
    }

    const employeesData = org.employeesPromise.then((employees) => {
      const usersWithAccess = employees.filter((e) => e.accessLevel === 'USER');

      // DEBUG: Log all employees with their access levels
      if (usersWithAccess.length === 0) {
        console.log(`[DEBUG] Organization ${org.name} (${org.id}) has no users with USER access level`);
        console.log(
          '[DEBUG] All employees:',
          employees.map((e) => ({
            name: `${e.firstName} ${e.lastName}`,
            accessLevel: e.accessLevel,
            email: e.email,
          })),
        );
      }

      if (usersWithAccess.length === 0) {
        return html`<div class="org-info-row">${t('account.selectOrganizationDashboard.noUsersWithAccess')}</div>`;
      }
      const names = usersWithAccess.map((e) => `${e.firstName} ${e.lastName}`).filter((n) => n.trim());
      const displayNames = names.slice(0, 5);
      const text = joinWithAnd(displayNames);
      const more = names.length > 5 ? '…' : '';
      return html`<div class="org-info-row">${text}${more}</div>`;
    });

    return html`${until(employeesData, html`<div class="org-info-row"><span class="skeleton"></span></div>`)}`;
  }

  private renderMeetings(org: OrganizationWithData) {
    // For contact persons, don't show meetings
    if (org.userType === 'CONTACT') {
      return nothing;
    }

    // For employees with READONLY access, show their own meeting info instead of stats
    if (org.accessLevel === 'READONLY') {
      const myMeetingData = Promise.all([org.employeesPromise, org.meetingOccurrencesPromise]).then(
        ([employees, occurrences]) => {
          // Find UUID for the logged-in user by matching first and last name
          const currentUserEmployee = employees.find(
            (e) => e.firstName === org.firstName && e.lastName === org.lastName,
          );
          if (!currentUserEmployee) {
            return html`<div class="org-info-row">${t('account.selectOrganizationDashboard.userNotFound')}</div>`;
          }

          const today = LocalDate.now();

          // Filter on meetings where the user is a participant
          const myMeetings = occurrences.filter(
            (occ) => occ.participants && occ.participants.includes(currentUserEmployee.uuid),
          );

          const upcomingMeetings = myMeetings
            .filter((occ) => {
              if (!occ.date) return false;
              const meetingDate = LocalDate.fromString(occ.date);
              return meetingDate.isSameOrAfter(today);
            })
            .sort((a, b) => {
              const dateA = LocalDate.fromString(a.date!);
              const dateB = LocalDate.fromString(b.date!);
              return dateA.compareTo(dateB);
            });

          const pastMeetings = myMeetings
            .filter((occ) => {
              if (!occ.date) return false;
              const meetingDate = LocalDate.fromString(occ.date);
              return meetingDate.isBefore(today);
            })
            .sort((a, b) => {
              const dateA = LocalDate.fromString(a.date!);
              const dateB = LocalDate.fromString(b.date!);
              return dateB.compareTo(dateA);
            });

          const nextMeeting = upcomingMeetings[0];
          const lastMeeting = pastMeetings[0];

          if (!nextMeeting && !lastMeeting) {
            return html`<div class="org-info-row">
              ${t('account.selectOrganizationDashboard.noPlannedInterviews')}
            </div>`;
          }

          if (nextMeeting) {
            const dateTimeDisplay = dateTimeDisplayShort(nextMeeting.date, nextMeeting.time);
            const status = nextMeeting.noticeLastSent
              ? t('account.selectOrganizationDashboard.noticeSent')
              : t('account.selectOrganizationDashboard.notInvited');
            return html`<div class="org-info-row">
              <strong>${t('account.selectOrganizationDashboard.nextInterview')}</strong> ${dateTimeDisplay} - ${status}
            </div>`;
          }

          if (lastMeeting) {
            const dateTimeDisplay = dateTimeDisplayShort(lastMeeting.date, lastMeeting.time);
            const status = lastMeeting.reportLastSent
              ? t('account.selectOrganizationDashboard.reportSent')
              : lastMeeting.doneDate
                ? t('account.selectOrganizationDashboard.markedDone')
                : t('account.selectOrganizationDashboard.notCompleted');
            return html`<div class="org-info-row">
              <strong>${t('account.selectOrganizationDashboard.previousInterview')}</strong> ${dateTimeDisplay} -
              ${status}
            </div>`;
          }

          return nothing;
        },
      );

      return html`${until(
        myMeetingData,
        html`<div class="org-info-row"><span class="skeleton" style="width: 80%;"></span></div>`,
      )}`;
    }

    // For leaders/admins, show statistics
    const meetingsData = org.meetingStatsPromise.then((stats) => {
      if (stats.totalEmployees === 0) {
        return html`<div class="org-info-row">${t('account.selectOrganizationDashboard.noEmployees')}</div>`;
      }

      // Horisontal layout med 6 bokser
      return html`<div class="org-info-row meeting-stats-container">
        <div class="meeting-stats-header">
          ${t('account.selectOrganizationDashboard.employeeInterviews')}
          <span class="stats-total"
            >${t('account.selectOrganizationDashboard.employeesCount', { count: stats.totalEmployees })}</span
          >
        </div>

        <div class="meeting-stats-row">
          <!-- Box 1: Ikke planlagt -->
          <div class="stat-box ${stats.employeesWithoutInterview > 0 ? 'stat-alert-box' : ''}">
            <div class="stat-number">${stats.employeesWithoutInterview}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statNotPlanned')}</div>
          </div>

          <!-- Separator -->
          <div class="stat-separator"></div>

          <!-- Box 2: Passert -->
          <div class="stat-box">
            <div class="stat-number">${stats.pastTotal}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statPast')}</div>
          </div>

          <!-- Box 3: Sendt referat -->
          <div class="stat-box">
            <div class="stat-number">${stats.pastWithReport}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statReportSent')}</div>
          </div>

          <!-- Box 4: Merket ferdig -->
          <div class="stat-box">
            <div class="stat-number">${stats.pastDone}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statMarkedDone')}</div>
          </div>

          <!-- Separator -->
          <div class="stat-separator"></div>

          <!-- Box 5: Planlagt -->
          <div class="stat-box">
            <div class="stat-number">${stats.scheduledTotal}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statPlanned')}</div>
          </div>

          <!-- Box 6: Innkalt -->
          <div class="stat-box">
            <div class="stat-number">${stats.scheduledWithNotice}</div>
            <div class="stat-label">${t('account.selectOrganizationDashboard.statInvited')}</div>
          </div>
        </div>
      </div>`;
    });

    return html`${until(
      meetingsData,
      html`<div class="org-info-row"><span class="skeleton" style="width: 80%;"></span></div>`,
    )}`;
  }

  private renderStatus(org: OrganizationWithData) {
    if (org.closed) {
      return html`<span class="org-status closed">${t('account.selectOrganizationDashboard.statusClosed')}</span>`;
    }
    if (org.pending) {
      return html`<span class="org-status pending">${t('account.selectOrganizationDashboard.statusPending')}</span>`;
    }
    return nothing;
  }

  render() {
    return html`
      <div id="accountSelector">
        <ul>
          ${this.sortedOrganizations.map(
            (organization) =>
              html` <li>
                <a href="${getOrganizationUrl(organization)}" @click=${this.fireCloseSettings}>
                  <div class="org-name">${organization.name}</div>
                  <div class="org-info">${this.renderEmployees(organization)} ${this.renderMeetings(organization)}</div>
                  ${this.renderStatus(organization)}
                </a>
              </li>`,
          )}
          ${this.organizations.length === 0
            ? html` <li class="noOrgMessage">${t('account.selectOrganizationDashboard.noOrganizations')}</li> `
            : nothing}
        </ul>
      </div>
    `;
  }

  private fireCloseSettings() {
    this.dispatchEvent(new CustomEvent('close-settings', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-select-organization-dashboard': DSelectOrganizationDashboard;
  }
}
